import { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Heading,
  Panel,
  PanelActions,
  PanelHeader,
  PanelInset,
  PanelTitle,
  Search,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { ObjectAttributeList } from '@/features/objects';
import { filteredAttrsWithIdentifiers } from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';
import { ObjectAttributeChanges } from './object-attribute-changes';
import { useAttributesBreadcrumbs } from './use-attributes-breadcrumbs';
import { useObjectDetail } from './context';

const ENABLE_SEARCH_AFTER = 10;

const NO_PERMISSION_TOOLTIP = 'Contact an Admin to access this feature.';

/** Case-insensitive substring match on the attribute name, as the overview does. */
function matchingAttributes(
  attributes: ReadonlyArray<Record<string, string>>,
  searchTerm: string,
): Array<Record<string, string>> {
  if (isEmptyString(searchTerm)) return [...attributes];
  const loweredTerm = searchTerm.toLowerCase();
  return attributes.filter((attr) => {
    const [key = ''] = Object.keys(attr);
    return key.toLowerCase().includes(loweredTerm);
  });
}

export function ObjectAttributesTab() {
  const detail = useObjectDetail();
  const { record, hrefs } = detail;
  const [searchTerm, setSearchTerm] = useState('');
  useAttributesBreadcrumbs();

  const allAttrs = filteredAttrsWithIdentifiers(record);
  const isSearchEnabled = allAttrs.length >= ENABLE_SEARCH_AFTER;
  const trimmedTerm = searchTerm.trim();
  const matchedAttrs = matchingAttributes(allAttrs, trimmedTerm);

  return (
    <Grid columns="2fr 1fr" gap="300">
      <Panel>
        <PanelHeader pb="100">
          <PanelTitle size="md">Attributes</PanelTitle>
          <PanelActions>
            {isSearchEnabled ? (
              <Search
                data-test-attributes-search
                ariaLabel="Attribute name"
                size="sm"
                placeholder="Find by attribute name"
                value={searchTerm}
                onSearchInput={setSearchTerm}
                onSearch={setSearchTerm}
                shouldSearchOnInput
              />
            ) : null}
            <Tooltip isCustom isDisabled={detail.canConfigureObjects}>
              {(context) => (
                <>
                  <Button
                    data-test-object-attributes-edit
                    isDisabled={!detail.canConfigureObjects}
                    variant="secondary"
                    size="sm"
                    href={hrefs.attributesEdit}
                    ref={context.reference.setReference}
                    {...context.reference.getReferenceProps()}
                  >
                    Edit attributes
                  </Button>
                  <TooltipContent>{NO_PERMISSION_TOOLTIP}</TooltipContent>
                </>
              )}
            </Tooltip>
          </PanelActions>
        </PanelHeader>

        <PanelInset pb="200">
          {matchedAttrs.length === 0 ? (
            <Box textAlign="center" p="300" data-test-attributes-not-found>
              {/* `as="div"`: hydra's `enforce-pluma-elements` forbids a `p` element here. */}
              <Heading level="3" as="div" color="subtle">
                No attributes for this object matching "{searchTerm}"
              </Heading>
            </Box>
          ) : (
            <ObjectAttributeList attributes={matchedAttrs} isMultiIdentifier={detail.isMultiIdentifier} />
          )}
        </PanelInset>
      </Panel>

      <ObjectAttributeChanges />
    </Grid>
  );
}
